/**
 * The plan sets of a design, newest first: number, the design version it shows, who made it and when, what it cost,
 * its checks (problems and passed), "Open items: N" when it was generated with gaps, and its actions: Download (the
 * PDF), the free Open items checklist (PDF, when there are any), the editable sheets (.zip: DXF plans and SVG
 * diagrams), the SolarAPP+ data or summary, a comparison with the previous one, and "Send for engineer's stamp"
 * (coming soon: the place for a stamp partner, no backend yet).
 */
import { useMutation, useQuery } from "@tanstack/react-query";
import { ClipboardList, FileArchive, FileDown, FileJson, GitCompare, Stamp } from "lucide-react";
import { useState } from "react";
import { FormattedMessage } from "react-intl";
import { api, errorMessage, rawFetch, unwrap, type S } from "../api/client";
import { Badge, Button, Card, Spinner, cx, useToast } from "../components/ui";
import { shownVersion } from "../lib/format";
import { useT, type Translate } from "../i18n/I18nProvider";
import { useFormat } from "./format";
import { problemsText } from "../lib/permits";
import { openItemsLabel } from "../lib/permitEquipment";
import { checkChanges, checksDiff, luxText, type CheckChange, type CheckRow } from "../lib/permitWorkspace";
import type { Ws } from "./PermitWorkspace";

function download(url: string, filename: string) {
  const a = document.createElement("a");
  a.href = url;
  a.download = filename;
  a.rel = "noopener";
  document.body.appendChild(a);
  a.click();
  a.remove();
}

/** A file the API answers with Content-Disposition (the checklist, the editable sheets), saved under its name. */
async function fetchFile(path: string, fallback: string): Promise<string> {
  const response = await rawFetch(path);
  const name = /filename="([^"]+)"/.exec(response.headers.get("Content-Disposition") ?? "")?.[1] ?? fallback;
  const url = URL.createObjectURL(await response.blob());
  download(url, name);
  window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
  return name;
}


export function History({ ws }: { ws: Ws }) {
  const t = useT();
  const items = ws.planSets.data?.items ?? [];
  return (
    <Card title={t("permit.history.title")} description={items.length ? t("permit.history.description") : undefined}>
      {ws.planSets.isLoading ? (
        <Spinner />
      ) : !items.length ? (
        <p className="muted" style={{ margin: 0 }}>{t("permit.history.empty")}</p>
      ) : (
        <ul className="pw-history">
          {items.map((p, i) => (
            <HistoryRow key={p.id} ws={ws} p={p} previous={items[i + 1]} />
          ))}
        </ul>
      )}
    </Card>
  );
}

function HistoryRow({ ws, p, previous }: { ws: Ws; p: S["PlanSet"]; previous?: S["PlanSet"] }) {
  const t = useT();
  const f = useFormat();
  const toast = useToast();
  const [compare, setCompare] = useState(false);
  const offset = ws.doc.version_offset;
  const pdf = useMutation({
    mutationFn: async () => {
      const link = await unwrap(api.GET("/v1/designs/{design_id}/versions/{version_number}/plan-set/pdf", { params: { path: { design_id: ws.designId, version_number: p.version_number }, query: { plan_set_id: p.id } } }));
      download(link.url, link.filename);
      return link.filename;
    },
    onSuccess: (name) => toast(t("permit.history.downloading", { name })),
    onError: (e) => toast(errorMessage(e), true),
  });
  const fileBase = `/api/v1/designs/${ws.designId}/versions/${p.version_number}/plan-set`;
  const checklist = useMutation({
    mutationFn: () => fetchFile(`${fileBase}/open-items.pdf?plan_set_id=${p.id}`, `open-items-${p.number}.pdf`),
    onSuccess: (name) => toast(t("permit.history.downloading", { name })),
    onError: (e) => toast(errorMessage(e), true),
  });
  const editable = useMutation({
    mutationFn: () => fetchFile(`${fileBase}/editable.zip?plan_set_id=${p.id}`, `plan-set-${p.number}-editable.zip`),
    onSuccess: (name) => toast(t("permit.history.downloading", { name })),
    onError: (e) => toast(errorMessage(e), true),
  });
  const openLabel = openItemsLabel(t, p.open_items);
  const summary = useMutation({
    mutationFn: async () => {
      const data = await unwrap(api.GET("/v1/designs/{design_id}/versions/{version_number}/plan-set/electrical", { params: { path: { design_id: ws.designId, version_number: p.version_number }, query: { plan_set_id: p.id } } }));
      const url = URL.createObjectURL(new Blob([JSON.stringify(data, null, 2)], { type: "application/json" }));
      download(url, `plan-set-${p.number}-electrical.json`);
      window.setTimeout(() => URL.revokeObjectURL(url), 10_000);
    },
    onError: (e) => toast(errorMessage(e), true),
  });
  const s = p.checks_summary;
  const problems = Math.max(0, s.problem - (p.open_items ?? 0)); // design errors; the open items have their own badge
  const rendering = p.status === "pending" || p.status === "running";
  const outdated = p.version_number < ws.version;
  const diff = previous ? checksDiff(s, previous.checks_summary) : [];
  // check by check, from the two plan sets' electrical summaries (asked when Compare opens)
  const electrical = (q: S["PlanSet"] | undefined) => ({
    queryKey: ["plan-set-electrical", q?.id],
    enabled: compare && !!q,
    staleTime: Infinity,
    queryFn: () => unwrap(api.GET("/v1/designs/{design_id}/versions/{version_number}/plan-set/electrical", { params: { path: { design_id: ws.designId, version_number: q!.version_number }, query: { plan_set_id: q!.id } } })),
  });
  const nowE = useQuery(electrical(p));
  const beforeE = useQuery(electrical(previous));
  const changes = nowE.data && beforeE.data ? checkChanges(nowE.data.checks as CheckRow[], beforeE.data.checks as CheckRow[]) : null;
  return (
    <li className="pw-history-row">
      <div className="pw-h-main">
        <div className="pw-h-title">
          <b>#{p.number}</b>
          <span>{t("permit.history.designVersion", { version: shownVersion(p.version_number, offset) })}</span>
          {outdated && <Badge tone="warn">{t("permit.history.outdated", { version: shownVersion(ws.version, offset) })}</Badge>}
          {openLabel && (
            <span title={t("permit.history.openItemsHint")}>
              <Badge tone="warn">{openLabel}</Badge>
            </span>
          )}
        </div>
        <div className="small muted">
          <span dir="auto">{p.created_by_name ?? "—"}</span> · {f.date(p.created_at, true)} · {p.country} ·{" "}
          {p.refunded ? t("permit.history.refunded", { cost: p.lux ? luxText(t, p.lux) : t("permit.history.free") }) : p.lux ? luxText(t, p.lux) : t("permit.history.free")}
        </div>
        <div className="pw-h-chips">
          {problems > 0 && <span className="pw-count fail">{problemsText(t, problems)}</span>}
          <span className="pw-count ok">{t("permit.history.passed", { count: s.pass })}</span>
          {rendering ? (
            <Badge tone="info">{t("permit.history.rendering")}</Badge>
          ) : p.status === "failed" ? (
            <Badge tone="danger">{p.refunded ? t("permit.history.failedRefunded") : t("permit.history.failed")}</Badge>
          ) : null}
          {p.pages ? <span className="small muted">{t("permit.history.pages", { count: p.pages })}</span> : null}
        </div>
      </div>
      <div className="pw-h-actions">
        <Button size="sm" icon={<FileDown size={14} />} disabled={!p.available} loading={pdf.isPending} onClick={() => pdf.mutate()}>
          {t("common.download")}
        </Button>
        <span title={t("permit.history.stampSoon")}>
          <Button size="sm" variant="ghost" className="pw-soon" icon={<Stamp size={14} />} disabled aria-describedby={`pw-stamp-${p.id}`}>
            {t("permit.history.stamp")}
          </Button>
          <span id={`pw-stamp-${p.id}`} hidden>
            {t("permit.history.stampSoon")}
          </span>
        </span>
        {openLabel && (
          <Button size="sm" variant="ghost" icon={<ClipboardList size={14} />} loading={checklist.isPending} onClick={() => checklist.mutate()} title={t("permit.history.checklistHint")}>
            {t("permit.history.checklist")}
          </Button>
        )}
        <Button size="sm" variant="ghost" icon={<FileArchive size={14} />} loading={editable.isPending} onClick={() => editable.mutate()} title={t("permit.history.editableHint")}>
          {t("permit.history.editable")}
        </Button>
        <Button size="sm" variant="ghost" icon={<FileJson size={14} />} loading={summary.isPending} onClick={() => summary.mutate()} title={p.country === "US" ? t("permit.history.solarAppDataHint") : t("permit.history.summaryHint")}>
          {p.country === "US" ? t("permit.history.solarAppData") : t("permit.history.summary")}
        </Button>
        {previous && (
          <Button size="sm" variant="ghost" icon={<GitCompare size={14} />} onClick={() => setCompare((v) => !v)} aria-expanded={compare}>
            {t("permit.history.compare", { number: previous.number })}
          </Button>
        )}
      </div>
      {compare && previous && (
        <div className={cx("pw-h-extra", "pw-diff")}>
          <b className="small">{t("permit.history.compared", { number: previous.number })}</b>
          <ul className="small">
            {previous.version_number !== p.version_number && (
              <li>
                {t("permit.history.versionChange", { from: shownVersion(previous.version_number, offset), to: shownVersion(p.version_number, offset) })}
              </li>
            )}
            {diff.map((d) => (
              <li key={d.status} className={d.status === "problem" ? (d.delta < 0 ? "better" : "worse") : undefined}>
                {t("permit.history.diffLine", {
                  sign: d.delta > 0 ? "+" : "−",
                  count: Math.abs(d.delta),
                  status: d.status,
                  before: previous.checks_summary[d.status as "problem" | "pass"],
                  after: s[d.status as "problem" | "pass"],
                })}
              </li>
            ))}
            {changedInputs(p.inputs, previous.inputs).map((k) => (
              <li key={k}>{t("permit.history.changedInput", { input: k.replace(/_id$/, "").replace(/_/g, " ") })}</li>
            ))}
            {changes == null ? (
              <li className="muted">{t("permit.history.comparing")}</li>
            ) : (
              changes.map((c, i) => (
                <li key={i} className={c.kind === "status" ? (c.to === "problem" ? "worse" : c.from === "problem" ? "better" : undefined) : undefined}>
                  <FormattedMessage id="permit.history.checkChange" values={{ title: c.title, change: describe(t, c), b: (x) => <b>{x}</b> }} />
                </li>
              ))
            )}
            {changes != null && !changes.length && previous.version_number === p.version_number && !changedInputs(p.inputs, previous.inputs).length && <li>{t("permit.history.same")}</li>}
          </ul>
        </div>
      )}
    </li>
  );
}

function describe(t: Translate, c: CheckChange) {
  switch (c.kind) {
    case "added":
      return t("permit.history.change.added", { status: c.status, raw: c.status });
    case "removed":
      return t("permit.history.change.removed");
    case "status":
      return t("permit.history.change.status", { from: c.from, fromRaw: c.from, to: c.to, toRaw: c.to });
    case "detail":
      return (
        <span className="pw-diff-text">
          {c.from} → {c.to}
        </span>
      );
  }
}

function changedInputs(a: Record<string, unknown>, b: Record<string, unknown>): string[] {
  const keys = new Set([...Object.keys(a ?? {}), ...Object.keys(b ?? {})]);
  return [...keys].filter((k) => JSON.stringify(a?.[k] ?? null) !== JSON.stringify(b?.[k] ?? null)).sort();
}
